<template>
    <div id="food-table-index-app-dt">
        <div class="example-section">
            <DataTable
                ref="datatable"
                :columns="userColumns"
                :items="items"
                :current-page="currentPage"
                :total-pages="totalPages"
                :enable-multi-select="true"
                :enable-drag-drop="false"
                :selected-items="selectedItems"
                @page-changed="handlePageChange"
                @item-selected="toggleItemSelection"
                @select-all-toggled="toggleSelectAll"
                @sort="onSort"
            >
                <template #toolbar>
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                        <!-- Per Page Selector -->

                        <!-- Create Button -->
                        <button
                            class="ppv2-min-h-[40px] ppv2-flex ppv2-items-center !ppv2-text-white hover:ppv2-text-white ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px] ppv2-py-2 ppv2-rounded-xl ppv2-bg-[#7D2DBD] ppv2-text-white hover:ppv2-bg-[#6b26a3] ppv2-transition-all ppv2-shadow-lg"
                            @click="openAddModal"
                        >
                            <Plus :size="17" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }"> Créer des QR Codes </span>
                        </button>

                        <div class="ppv2-flex-1"></div>

                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="onSearch" />
                        </div>

                        <!-- Export Button -->
                        <ExportSelect
                            ref="exportDropdown"
                            label="Exporter"
                            :options="exportItems"
                            @export-selected="handleExport"
                        />
                    </div>
                </template>

                <template #column-url_front_link="{ item }">
                    <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                        <a
                            v-if="item.url_front_link"
                            :href="item.url_front_link"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="ppv2-text-[#7D2DBD] hover:ppv2-underline ppv2-flex ppv2-items-center ppv2-gap-1"
                        >
                            {{ truncate(item.url_front_link, 50) }}
                            <ExternalLink :size="14" />
                        </a>
                        <span v-else class="text-muted">N/A</span>
                        <button
                            @click.stop="copyToClipboard(item)"
                            class="ppv2-p-1 hover:ppv2-bg-gray-100 ppv2-rounded ppv2-transition-colors"
                            :title="copiedItemId === item.id ? 'Copié !' : 'Copier le lien'"
                        >
                            <Copy :size="16" :color="copiedItemId === item.id ? '#10B981' : '#7D2DBD'" />
                        </button>
                        <span v-if="copiedItemId === item.id" class="ppv2-text-[#10B981] ppv2-text-xs">Copié</span>
                    </div>
                </template>

                <template #column-actions="{ item }">
                    <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                        <button
                            @click.stop="downloadImg(item)"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            title="Télécharger QR Code"
                        >
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M1.21289 17.6012C1.21289 14.7752 2.69884 12.2962 4.93205 10.9029C5.53943 6.12426 9.62017 2.42969 14.5638 2.42969C19.5074 2.42969 23.5882 6.12426 24.1956 10.9029C26.4288 12.2962 27.9148 14.7752 27.9148 17.6012C27.9148 21.7541 24.7059 25.1575 20.6324 25.4675L8.49522 25.4904C4.42171 25.1575 1.21289 21.7541 1.21289 17.6012ZM20.4483 23.0469C23.2804 22.8315 25.4873 20.4609 25.4873 17.6012C25.4873 15.692 24.5023 13.9554 22.9107 12.9625L21.9328 12.3524L21.7875 11.209C21.3285 7.59775 18.24 4.85713 14.5638 4.85713C10.8877 4.85713 7.79912 7.59775 7.34012 11.209L7.19479 12.3524L6.21692 12.9625C4.62534 13.9554 3.64034 15.692 3.64034 17.6012C3.64034 20.4609 5.84728 22.8315 8.67935 23.0469L8.88968 23.063H20.238L20.4483 23.0469ZM15.7776 14.5669H19.4187L14.5638 20.6355L9.70895 14.5669H13.3501V9.71202H15.7776V14.5669Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </button>
                        <button
                            @click.stop="editItem(item)"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            title="Modifier"
                        >
                            <SquarePen :size="18" />
                        </button>
                        <button
                            @click.stop="deleteItem(item)"
                            class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            title="Supprimer"
                        >
                            <Trash2 :size="18" />
                        </button>
                    </div>
                </template>

                <template #bulk-actions="{ selectedCount }">
                    <span class="poppinsRegular textGray" :style="{ fontSize: normalizeFontSize(15) }">
                        {{ selectedCount }} élément{{ selectedCount > 1 ? 's' : '' }} sélectionné{{
                            selectedCount > 1 ? 's' : ''
                        }}
                    </span>
                    <span class="poppinsRegular ppv2-text-white ppv2-text-opacity-50" :style="{ fontSize: normalizeFontSize(15) }"
                        >|</span
                    >
                    <button
                        @click="handleDeleteSelected"
                        class="ppv2-flex ppv2-items-center ppv2-gap-2 px-3 ppv2-text-[#FF9C9C] hover:ppv2-text-red-600 ppv2-transition-colors"
                    >
                        <Trash2 :size="14" />
                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }"> Supprimer la sélection </span>
                    </button>
                </template>
            </DataTable>
        </div>

        <!-- Edit Modal -->
        <CustomDialog
            v-model="isModalVisible"
            defaultTitle="Modifier le QR Code"
            heightClass="ppv2-max-h-[40vh]"
            customClassTitle="poppinsSemiBold"
        >
            <template #content>
                <input v-model="editingItem.name" type="text" class="form-control rounded-12" @keydown.enter="saveItem" />
            </template>
            <template #actions>
                <BaseButton id="button" type="button" variant="outline" customClass="!ppv2-min-h-[40px]" @click="closeModal">
                    Annuler
                </BaseButton>
                <BaseButton type="submit" variant="primary" @click="saveItem" customClass="!ppv2-min-h-[40px]" :customHeight="43">
                    Mettre à jour
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Delete Modal -->
        <CustomDialog
            v-model="isDeleteModalVisible"
            defaultTitle="Confirmer la suppression"
            customClassTitle="poppinsSemiBold"
            noScroll
        >
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletingItem ? `Êtes-vous sûr de vouloir supprimer cette table (${deletingItem.name}) ?` : '' }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="closeDeleteModal"
                    customClass="!ppv2-h-[45px] !ppv2-min-w-[170px]"
                >
                    Annuler
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    customClass="!ppv2-h-[45px] !ppv2-min-w-[170px]"
                >
                    Supprimer
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Add Modal -->

        <CustomDialog
            v-model="isAddModalVisible"
            defaultTitle="Ajouter des tables"
            heightClass="ppv2-max-h-[40vh]"
            customClassTitle="poppinsSemiBold"
        >
            <template #content>
                <input v-model="number_of_tables" type="number" class="form-control rounded-12" @keydown.enter="save" />
            </template>
            <template #actions>
                <BaseButton id="button" type="button" variant="outline" @click="closeAddModal"> Annuler </BaseButton>
                <BaseButton type="submit" variant="primary" @click="save"> Ajouter </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import DataTable from '../../Shared/DataTable.vue'
import ExportSelect from '../../Shared/ExportSelect.vue'
import ApiManagerFood from '../Api/ApiManagerFood'
import Search from '../../V3/Experience/components/Search.vue'
import normalizeSize from '../../../utils/normalizeSize'
import { SquarePen, Trash2, Download, Copy, ExternalLink, Plus, ChevronDown } from 'lucide-vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'IndexFoodTable',
    components: {
        BaseButton,
        DataTable,
        ExportSelect,
        Search,
        SquarePen,
        Trash2,
        Download,
        Copy,
        ExternalLink,
        Plus,
        ChevronDown,
        CustomDialog,
    },
    data() {
        return {
            // UI State
            isModalVisible: false,
            editingItem: null,
            isDeleteModalVisible: false,
            deletingItem: null,
            isAddModalVisible: false,
            number_of_tables: 1,
            copiedItemId: null,
            showPerPageDropdown: false,

            // DataTable State
            items: [],
            loading: false,
            currentPage: 1,
            perPage: 10,
            totalEntries: 0,
            searchQuery: '',
            sortKey: '',
            sortOrder: 'asc',
            searchTimeout: null,
            selectedItems: [],
            selectAll: false,

            // API Configuration
            apiHeaders: {
                Authorization: 'Bearer your-token-here',
                'X-Requested-With': 'XMLHttpRequest',
            },
            usersApiUrl: '/back-office/food/tables',
            userColumns: [
                {
                    key: 'name',
                    label: 'Nom',
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'url_front_link',
                    label: 'Lien de commande',
                    sortable: false,
                    type: 'text',
                },
                {
                    key: 'actions',
                    label: 'Actions',
                    sortable: false,
                    type: 'actions',
                },
            ],

            exportItems: [
                { label: 'Planche de QR Codes (PDF)', value: 'pdf', loading: false },
                { label: 'Excel', value: 'xlsx', loading: false },
                { label: 'CSV', value: 'csv', loading: false },
            ],
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.totalEntries / this.perPage)
        },
    },
    mounted() {
        this.fetchData()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchData() {
            this.loading = true
            try {
                const params = {
                    page: this.currentPage,
                    per_page: this.perPage,
                    search: this.searchQuery,
                    sort_by: this.sortKey,
                    sort_order: this.sortOrder,
                }
                const response = await this.makeApiRequest(params)
                this.handleApiResponse(response)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.onError(error)
            } finally {
                this.loading = false
            }
        },
        async makeApiRequest(params) {
            const queryString = new URLSearchParams(params).toString()
            const url = `${this.usersApiUrl}?${queryString}`

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.apiHeaders,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            return await response.json()
        },
        handleApiResponse(response) {
            let _response = response.data
            if (_response.data && Array.isArray(_response.data)) {
                this.items = _response.data
                this.totalEntries = _response.total || _response.data.length
            } else if (Array.isArray(_response)) {
                this.items = _response
                this.totalEntries = _response.length
            } else {
                this.items = []
                this.totalEntries = 0
            }
            this.onDataLoaded({ data: this.items, total: this.totalEntries })
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        onSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.fetchData()
            }, 500)
        },
        onSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchData()
        },
        selectPerPage(value) {
            this.perPage = value
            this.showPerPageDropdown = false
            this.currentPage = 1
            this.fetchData()
        },
        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
            this.selectAll = this.items.length > 0 && this.selectedItems.length === this.items.length
        },
        toggleSelectAll() {
            if (this.selectAll) {
                this.selectedItems = []
            } else {
                this.selectedItems = this.items.map((item) => item.id)
            }
            this.selectAll = !this.selectAll
        },
        truncate(text, length) {
            if (text.length > length) {
                return text.substring(0, length) + '...'
            }
            return text
        },
        copyToClipboard(item) {
            navigator.clipboard
                .writeText(item.url_front_link)
                .then(() => {
                    this.copiedItemId = item.id
                    setTimeout(() => {
                        this.copiedItemId = null
                    }, 2000)
                })
                .catch((err) => {
                    console.error('Failed to copy: ', err)
                })
        },
        onDataLoaded(response) {
            console.log('Data loaded:', response)
        },
        onError(error) {
            console.error('Datatable error:', error)
            // alert('Error loading data: ' + error.message)
        },
        async downloadImg(item) {
            try {
                const blob = await ApiManagerFood.downloadQrCode(item.qr_code_url)
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', `qr-code-${item.name}.png`)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        deleteItem(item) {
            this.deletingItem = item
            this.isDeleteModalVisible = true
        },
        confirmDelete() {
            ApiManagerFood.deleteTable(this.deletingItem.uuid)
                .then((response) => {
                    this.closeDeleteModal()
                    this.fetchData()
                })
                .catch((error) => {
                    console.error('Error deleting table:', error)
                    alert('Error deleting table: ' + error.message)
                })
        },
        closeDeleteModal() {
            this.isDeleteModalVisible = false
            this.deletingItem = null
        },
        editItem(item) {
            this.editingItem = { ...item }
            this.isModalVisible = true
        },
        saveItem() {
            ApiManagerFood.updateTable(this.editingItem.id, { name: this.editingItem.name })
                .then((response) => {
                    this.closeModal()
                    this.fetchData()
                })
                .catch((error) => {
                    if (error.response.data.errors.name) {
                        this.$toast.error(error.response.data.errors.name[0])
                    }
                    console.error('Error updating table name:', error)
                })
        },
        closeModal() {
            this.isModalVisible = false
            this.editingItem = null
        },
        openAddModal() {
            this.isAddModalVisible = true
        },
        closeAddModal() {
            this.isAddModalVisible = false
        },
        async save() {
            try {
                const payload = { number_of_tables: this.number_of_tables }
                await ApiManagerFood.addTablesByNumbers(payload)
                this.$toast.success(this.$t('food.tables.added_success'))
                this.fetchData()
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
            this.closeAddModal()
        },
        async exportQrPdf() {
            const blob = await ApiManagerFood.exportQrPdf()
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'tables-qr-codes.pdf')
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        },
        async exportExcel() {
            const blob = await ApiManagerFood.exportExcel()
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'tables-export.xlsx')
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        },
        async exportCsv() {
            const blob = await ApiManagerFood.exportCsv()
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'tables-export.csv')
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        },
        async handleExport({ value, item }) {
            if (item.loading) return

            item.loading = true
            try {
                if (value === 'pdf') await this.exportQrPdf()
                else if (value === 'xlsx') await this.exportExcel()
                else if (value === 'csv') await this.exportCsv()
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                item.loading = false
                this.$refs.exportDropdown.close()
            }
        },
        async handleDeleteSelected() {
            try {
                const ids = this.selectedItems
                if (ids.length === 0) return

                // Note: The API might expect array of IDs or objects.
                // Based on previous code: const ids = selectedItems.map((item) => item.id)
                // But here selectedItems stores IDs directly.
                await ApiManagerFood.deleteTables(ids)
                this.fetchData()
                this.selectedItems = []
                this.selectAll = false
                this.$toast.success(this.$t('food.tables.deleted_success'))
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
    },
}
</script>

<style scoped lang="scss">
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    width: 500px;
    position: relative;
}

.close-button {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
}

.modal-content h2 {
    margin-top: 0;
}

.modal-content input {
    width: 100%;
    padding: 8px;
    margin-bottom: 10px;
}

.modal-content button {
    margin-right: 10px;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
}

.example-section {
    margin-bottom: 40px;
}

.textGray {
    color: #1a1f37;
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}
</style>
